import { toast } from 'sonner';
import type { TFunction } from 'i18next';
import { isNeverInvited, type User } from '@bliks-io/contracts';
import { toastApiError, toastRequestError } from '@/api/mutations/toast-api-error';
import { translateErrorMessage } from '@/lib/errors';
import type { ApiError } from '@/lib/agent.types';

/**
 * Shared failure tail of the setup step mutations: surface the error banner,
 * toast the failure, and log. The structural-anchor steps diverge (they map
 * field errors back onto rows) and don't use this. Extracted because three
 * step handlers repeated this trio verbatim.
 */
export function reportStepError(
  error: unknown,
  failedToastKey: string,
  t: TFunction,
  setSubmitError: (error: string | undefined) => void
): void {
  setSubmitError(translateErrorMessage(error as ApiError, t));
  toastApiError(error, failedToastKey);
  console.error(error);
}

/**
 * Shared Weiter flow of the steps that validate a draft and then persist it
 * (optional imports + the Funktionen/Teams surface): an empty draft is plain
 * navigation (identical to Überspringen); otherwise validate — the caller owns
 * how many arrays it checks and reflects the marked rows as a side effect,
 * returning the payload to send or null when a row is invalid — toast on
 * invalid, else run the mutation and advance, with one copy of the error
 * handling.
 */
export async function runStepMutation<TValidated>({
  isEmpty,
  validate,
  mutate,
  navigateNext,
  fixErrorsToastKey,
  failedToastKey,
  t,
  setSubmitError
}: {
  isEmpty: boolean;
  validate: () => TValidated | null;
  mutate: (validated: TValidated) => Promise<void>;
  navigateNext: () => void;
  fixErrorsToastKey: string;
  failedToastKey: string;
  t: TFunction;
  setSubmitError: (error: string | undefined) => void;
}): Promise<void> {
  if (isEmpty) {
    navigateNext();
    return;
  }
  const validated = validate();
  if (validated === null) {
    toast.error(t(fixErrorsToastKey));
    return;
  }
  try {
    await mutate(validated);
    navigateNext();
  } catch (error) {
    reportStepError(error, failedToastKey, t, setSubmitError);
  }
}

/**
 * Best-effort "invite everyone the wizard added" for the completion step:
 * invite every never-invited member, toast the invited/skipped counts, and
 * swallow failures so a delivery error can't undo the already-committed
 * completion. Must run only after completion has cleared the setup gate.
 */
export async function sendNeverInvitedInvites(
  members: Pick<User, '_id' | 'status'>[],
  invite: (
    userIds: string[]
  ) => Promise<{ data: { invitedUserIds: string[]; skipped: unknown[] } }>,
  t: TFunction
): Promise<void> {
  const neverInvitedIds = members.filter((m) => isNeverInvited(m.status)).map((m) => m._id);
  if (neverInvitedIds.length === 0) return;
  try {
    const { invitedUserIds, skipped } = (await invite(neverInvitedIds)).data;
    if (invitedUserIds.length > 0) {
      toast.success(t('initialSetup.overview.invite.success', { count: invitedUserIds.length }));
    }
    if (skipped.length > 0) {
      toast.warning(t('initialSetup.overview.invite.skipped', { count: skipped.length }));
    }
  } catch (error) {
    toastRequestError(error, translateErrorMessage(error as ApiError, t));
    console.error(error);
  }
}
